import {useEffect, useRef, useState} from 'react';
import React from 'react';
import {get, includes, without} from 'lodash';
import {isEmpty} from 'lodash/fp';

import Text from '/component/base/Text';
import {ALERTS_STATE_IS_LOADING} from '/enum/alertsState.enum';
import {
  EXPIRING_REWARDS,
  EXPIRING_WELLNESS_CREDITS,
  NO_ORDER,
  PREMIER_CANCELLATIONS,
  RECENTLY_INACTIVE,
} from '/enum/peopleToContact.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {safeLayoutAnimation} from '/util/animation.util';
import {generateComponentCallback} from '/util/component.util';
import {generateCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';

export const useAlerts = ({
  alerts,
  dismissAlert,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  activatedOn,
  alertsState,
  featureFlag,
}: any) => {
  const {ts, tt} = useTranslation('component.screen.ClientProfile');
  const [isNeedRerender, setIsNeedRerender] = useState(true);
  const dismissedAlerts = useRef([]);
  const dismissedAlertFunc = useRef();
  const timer = 3000;

  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlag, 'ff_Premier_Plus', ''),
  );
  const updateExpiringRewards = shouldReleaseFeature(
    get(featureFlag, 'ff_Remove_Expiring_Rewards', ''),
  );

  useEffect(() => {
    isNeedRerender && setIsNeedRerender(false);
  }, [isNeedRerender]);
  const onDismissedPress = (alert: any) => {
    // If the user clicked the button while the undo was in progress
    // then we remove from the array
    //here we are using if conditions for different types of alerts because we have send different values for each type of Alert in the POST API
    if (alert.RECENTLY_INACTIVE) {
      dismissedAlerts.current = includes(
        dismissedAlerts.current,
        alert.RECENTLY_INACTIVE,
      )
        ? without(dismissedAlerts.current, alert.RECENTLY_INACTIVE)
        : [...dismissedAlerts.current, alert.RECENTLY_INACTIVE];
      // We send the data after three seconds of waiting.
      // Re-rendering again to send another request to the server.
      clearTimeout(dismissedAlertFunc.current);
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
      dismissedAlertFunc.current = setTimeout(() => {
        if (includes(dismissedAlerts.current, alert.RECENTLY_INACTIVE)) {
          dismissedAlerts.current = without(
            dismissedAlerts.current,
            alert.RECENTLY_INACTIVE,
          );
          setIsNeedRerender(true); // need to delete after api will be ready
          dismissAlert({
            type: alert.RECENTLY_INACTIVE,
            dismissStatus: true,
            lastOrderedOn: alert.lastOrderedOn,
            orderId: alert.orderId,
          });
        }
      }, timer);
    } else if (alert.PREMIER_CANCELLATIONS) {
      dismissedAlerts.current = includes(
        dismissedAlerts.current,
        alert.PREMIER_CANCELLATIONS,
      )
        ? without(dismissedAlerts.current, alert.PREMIER_CANCELLATIONS)
        : [...dismissedAlerts.current, alert.PREMIER_CANCELLATIONS];

      // We send the data after three seconds of waiting.
      // Re-rendering again to send another request to the server.
      clearTimeout(dismissedAlertFunc.current);
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
      dismissedAlertFunc.current = setTimeout(() => {
        if (includes(dismissedAlerts.current, alert.PREMIER_CANCELLATIONS)) {
          dismissedAlerts.current = without(
            dismissedAlerts.current,
            alert.PREMIER_CANCELLATIONS,
          );
          dismissAlert({
            type: alert.PREMIER_CANCELLATIONS,
            dismissStatus: true,
            premierOrderId: alert.premierOrderId,
          });
        }
      }, timer);
    } else if (alert.EXPIRING_REWARDS) {
      dismissedAlerts.current = includes(
        dismissedAlerts.current,
        alert.EXPIRING_REWARDS,
      )
        ? without(dismissedAlerts.current, alert.EXPIRING_REWARDS)
        : [...dismissedAlerts.current, alert.EXPIRING_REWARDS];

      // We send the data after three seconds of waiting.
      // Re-rendering again to send another request to the server.
      clearTimeout(dismissedAlertFunc.current);
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
      dismissedAlertFunc.current = setTimeout(() => {
        if (includes(dismissedAlerts.current, alert.EXPIRING_REWARDS)) {
          dismissedAlerts.current = without(
            dismissedAlerts.current,
            alert.EXPIRING_REWARDS,
          );
          dismissAlert({
            type: alert.EXPIRING_REWARDS,
            dismissStatus: true,
            rewardId: alert.rewardId,
          });
        }
      }, timer);
    } else if (alert.EXPIRING_WELLNESS_CREDITS) {
      dismissedAlerts.current = includes(
        dismissedAlerts.current,
        alert.EXPIRING_WELLNESS_CREDITS,
      )
        ? without(dismissedAlerts.current, alert.EXPIRING_WELLNESS_CREDITS)
        : [...dismissedAlerts.current, alert.EXPIRING_WELLNESS_CREDITS];

      // We send the data after three seconds of waiting.
      // Re-rendering again to send another request to the server.
      clearTimeout(dismissedAlertFunc.current);
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
      dismissedAlertFunc.current = setTimeout(() => {
        if (
          includes(dismissedAlerts.current, alert.EXPIRING_WELLNESS_CREDITS)
        ) {
          dismissedAlerts.current = without(
            dismissedAlerts.current,
            alert.EXPIRING_WELLNESS_CREDITS,
          );
          dismissAlert({
            type: alert.EXPIRING_WELLNESS_CREDITS,
            dismissStatus: true,
            latestExpiryDate: alert.latestExpiryDate,
          });
        }
      }, timer);
    } else if (alert) {
      dismissedAlerts.current = includes(dismissedAlerts.current, alert)
        ? without(dismissedAlerts.current, alert)
        : [...dismissedAlerts.current, alert];

      // We send the data after three seconds of waiting.
      // Re-rendering again to send another request to the server.
      clearTimeout(dismissedAlertFunc.current);
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'undefine... Remove this comment to see the full error message
      dismissedAlertFunc.current = setTimeout(() => {
        if (includes(dismissedAlerts.current, alert)) {
          dismissedAlerts.current = without(dismissedAlerts.current, alert);
          dismissAlert({
            type: alert,
            dismissStatus: true,
          });
        }
      }, timer);
    }
    setIsNeedRerender(true);
    safeLayoutAnimation();
  };

  let alertsToShow = [];

  const noOrderCheck = () => {
    const alertStatus = alerts[NO_ORDER]?.alertStatus;

    const currentAlertsState = alertsState[NO_ORDER]
      ? alertsState[NO_ORDER][ALERTS_STATE_IS_LOADING]
      : false;

    return alertStatus && !currentAlertsState;
  };
  const expiringWellnessCreditsCheck = () => {
    let alertStatus;
    let currentAlertsState;
    currentAlertsState = alertsState[EXPIRING_WELLNESS_CREDITS]
      ? alertsState[EXPIRING_WELLNESS_CREDITS][ALERTS_STATE_IS_LOADING]
      : false;
    Array.isArray(alerts[EXPIRING_WELLNESS_CREDITS])
      ? alerts[EXPIRING_WELLNESS_CREDITS]?.map(item => {
          alertStatus = item?.alertStatus;
        })
      : null;
    return alertStatus && !currentAlertsState;
  };
  const recentlyInactiveCheck = () => {
    const alertStatus = alerts[RECENTLY_INACTIVE]?.alertStatus;
    const currentAlertsState = alertsState[RECENTLY_INACTIVE]
      ? alertsState[RECENTLY_INACTIVE][ALERTS_STATE_IS_LOADING]
      : false;
    return alertStatus && !currentAlertsState;
  };
  const expiringRewardsCheck = () => {
    const alertStatus = alerts[EXPIRING_REWARDS]?.alertStatus;
    const currentAlertsState = alertsState[EXPIRING_REWARDS]
      ? alertsState[EXPIRING_REWARDS][ALERTS_STATE_IS_LOADING]
      : false;
    return alertStatus && !currentAlertsState;
  };
  if (noOrderCheck()) {
    alertsToShow.push({
      name: NO_ORDER,
      title: ts('noOrderTitle', 'No order'),
      description: ts(
        'noOrderDescription',
        'This Client hasn’t placed an order yet',
      ),
      bannerClose: generateCallback({func: onDismissedPress, params: NO_ORDER}),
    });
  }
  if (expiringWellnessCreditsCheck()) {
    const latestExpiryDate = new Date(
      Math.max(
        ...alerts[EXPIRING_WELLNESS_CREDITS].map((element: any) => {
          return new Date(element.expiresOn);
        }),
      ),
    );
    alertsToShow.push({
      name: EXPIRING_WELLNESS_CREDITS,
      title: ts('expiringWellnessCreditsTitle', 'Expiring wellness credits'),
      description: ts(
        'expiringWellnessCreditsDescription',
        Array.isArray(alerts[EXPIRING_WELLNESS_CREDITS])
          ? alerts[EXPIRING_WELLNESS_CREDITS]?.map(
              (item, _index) =>
                '$' +
                item?.balance +
                ' expiring on ' +
                getDateUtilDefaultInstance().formatDate(
                  item?.expiresOn,
                  'MMMM d, yyyy',
                ),
            ).join('\n')
          : null,
      ),

      bannerClose: generateCallback({
        func: onDismissedPress,
        params: {EXPIRING_WELLNESS_CREDITS, latestExpiryDate},
      }),
    });
  }
  if (recentlyInactiveCheck()) {
    const lastOrderedOn = alerts[RECENTLY_INACTIVE]?.lastOrderedOn;
    const orderId = alerts[RECENTLY_INACTIVE]?.orderId;

    const lastOrderedOndate = getDateUtilDefaultInstance().formatDate(
      lastOrderedOn,
      'MMMM d, yyyy',
    );
    alertsToShow.push({
      name: RECENTLY_INACTIVE,
      title: ts('recentlyInactiveTitle', 'Recently Inactive'),
      description: !isEmpty(lastOrderedOn)
        ? ts(
            'recentlyInactiveDescription',
            'Last order date ' + lastOrderedOndate,
          )
        : ts('noOrder', 'No Order'),
      bannerClose: generateCallback({
        func: onDismissedPress,
        params: {RECENTLY_INACTIVE, lastOrderedOn, orderId},
      }),
    });
  }
  if (expiringRewardsCheck() && !updateExpiringRewards) {
    const expiresOn = alerts[EXPIRING_REWARDS]?.expiresOn;
    const balance = alerts[EXPIRING_REWARDS]?.balance;
    const rewardId = alerts[EXPIRING_REWARDS]?.rewardsId;

    const expriesOndate = getDateUtilDefaultInstance().formatDate(
      expiresOn,
      'MMMM d, yyyy',
    );
    alertsToShow.push({
      name: EXPIRING_REWARDS,
      title: ts('expiringRewardsTitle', 'Expiring rewards'),
      description: tt(
        'expiringRewardsDescription',
        generateComponentCallback({
          component: (
            <Text>
              ${balance} expiring on {expriesOndate}
            </Text>
          ),
        }),
      ),
      bannerClose: generateCallback({
        func: onDismissedPress,
        params: {EXPIRING_REWARDS, rewardId},
      }),
    });
  }
  const premierCancellationsCheck = () => {
    const alertStatus = alerts[PREMIER_CANCELLATIONS]?.alertStatus;
    const currentAlertsState = alertsState[PREMIER_CANCELLATIONS]
      ? alertsState[PREMIER_CANCELLATIONS][ALERTS_STATE_IS_LOADING]
      : false;
    return alertStatus && !currentAlertsState;
  };

  if (premierCancellationsCheck()) {
    const cancelledDate = alerts[PREMIER_CANCELLATIONS]?.cancelledDate;
    const premierOrderId = alerts[PREMIER_CANCELLATIONS]?.premierOrderId;

    const cancelledDateformat = getDateUtilDefaultInstance().formatDate(
      cancelledDate,
      'MMMM d, yyyy',
    );

    alertsToShow.push({
      name: PREMIER_CANCELLATIONS,
      title: ts(
        'premierCancellationsTitle',
        updatePremierPlus ? 'Premier+ cancellation' : 'Premier cancelation',
      ),
      description: ts(
        'premierCancellationsDescription',
        'Cancelled on ' + cancelledDateformat,
      ),
      bannerClose: generateCallback({
        func: onDismissedPress,
        params: {PREMIER_CANCELLATIONS, premierOrderId},
      }),
    });
  }
  const keyExtractor = (item: any) => item.name;

  return {keyExtractor, alertsToShow, dismissedAlerts, ts, tt};
};
